import React, { forwardRef } from 'react'
import { FieldErrors } from 'react-hook-form'
import classNames from 'classnames'
import classes from './styles.module.scss'
interface ISelectInputProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  name: string
  fullWidth?: boolean
  errors?: FieldErrors
  isLoading?: boolean
  withError?: boolean
  isSuccess?: boolean
  isWarning?: boolean
  isError?: boolean
  required?: boolean
  bordered?: boolean
  leftIcon?: React.ReactNode
  options: { value: string, label: string }[]
}

const SelectInput = forwardRef((props: ISelectInputProps, ref: React.Ref<HTMLSelectElement>) => {
  const {
    label,
    name,
    fullWidth,
    errors,
    disabled,
    isLoading,
    withError = true,
    isError = false,
    isWarning = false,
    isSuccess = false,
    required = false,
    bordered = false,
    leftIcon,
    options,
    ...rest
  } = props

  const formErrorMessage = errors?.[name]?.message
  // Keyed on the error itself, not its text: a rule with no configured message
  // would otherwise block the submit with nothing on screen to explain it.
  const hasError = isError || !!errors?.[name]

  return (
    <div
      data-testid="select-input-container"
      className={classNames({
        [classes.container]: true,
        [classes.fullWidth]: fullWidth,
        [classes.input__error]: hasError,
        [classes.input__warning]: isWarning,
        [classes.input__success]: isSuccess,
      })}>
      {label &&
        <label
          className={classNames({
            [classes.label]: true,
            [classes.label__disabled]: disabled
          })}
          htmlFor={name}
        >
          {label + (required ? ' *' : '')}
        </label>
      }
      <div className={classes.input__container}>
        <LeftIcon leftIcon={leftIcon} />
        <select
          className={classNames({
            [classes.select]: true,
            [classes.select__leftSpace]: leftIcon,
            [classes.select__bordered]: bordered,
          })}
          disabled={disabled}
          id={name}
          name={name}
          ref={ref}
          {...rest}
        >
          {options.map((option: ISelectInputProps['options'][0]) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <span className={classNames({
        [classes.error]: true,
        [classes.error__hide]: !withError,
      })}>
        {errors?.[name]?.message as string}
      </span>
    </div>
  )
})

const LeftIcon = ({ leftIcon }: { leftIcon: React.ReactNode }) => {
  return (
    <span className={classes.input__left}>
      {leftIcon}
    </span>
  )
}

SelectInput.displayName = 'SelectInput'

export default SelectInput